// ─── AutoChinBot · Task Runtime ────────────────────────────────
// Wires Engine 1 ("edata") and Engine 2 ("etask") onto an existing AutoChinBot
// market engine, loads the per-symbol task config, and exposes the task events
// to the UI. One runtime per (strategyId, accountId) — a module-level
// singleton so it keeps running in the background exactly like the bot engine.
//
// ⚠️ Part 4 hook point: `notifyTelegram()` currently sends a PLAIN notification
// so the pipeline is testable end-to-end. Part 4 replaces it with the full
// interactive proposal conversation (buttons / multi-step workflow) built on
// the `telegram-bot` edge function.

import { supabase } from '@/lib/supabase';
import { sendTelegram, TELEGRAM_CATEGORIES } from '@/lib/telegram-sender';
import { buildSessionKey } from '@/lib/session-key';
import { getOrCreateAutoChinEngine, type AutoChinBotEngine } from '@/lib/auto-chin-bot-engine';
import { AutoChinEdataEngine } from '@/lib/autochin-edata-engine';
import {
  AutoChinEtaskEngine,
  type AutoChinEtaskConfig,
  type AutoChinTaskEvent,
} from '@/lib/autochin-etask-engine';
import {
  AutoChinRiskController,
  type AutoChinRiskConfig,
  type AutoChinRiskResult,
} from '@/lib/autochin-risk-controller';
import { AutoChinOrderEngine } from '@/lib/autochin-order-engine';
import { AutoChinKn1Controller } from '@/lib/autochin-kn1-controller';
import { AutoChinKn2Controller } from '@/lib/autochin-kn2-controller';
import { AutoChinKn3Controller } from '@/lib/autochin-kn3-controller';
import { AutoChinPendingController } from '@/lib/autochin-pending-controller';
import { AutoChinAlertController } from '@/lib/autochin-alert-controller';

const MAX_EVENTS = 50;

export class AutoChinTaskRuntime {
  readonly strategyId: string;
  readonly accountId: string;
  readonly bot: AutoChinBotEngine;
  readonly edata: AutoChinEdataEngine;
  readonly etask: AutoChinEtaskEngine;
  readonly risk: AutoChinRiskController;
  readonly order: AutoChinOrderEngine;
  readonly kn1: AutoChinKn1Controller;
  readonly kn2: AutoChinKn2Controller;
  readonly kn3: AutoChinKn3Controller;
  readonly pending: AutoChinPendingController;
  readonly alerts: AutoChinAlertController;

  private userId: string;
  private events: AutoChinTaskEvent[] = [];
  private listeners = new Set<(events: AutoChinTaskEvent[]) => void>();
  private unsubEvents: (() => void) | null = null;
  private unsubRiskEvent: (() => void) | null = null;
  private attached = false;
  private configs: Record<number, AutoChinEtaskConfig> = {};
  private riskConfigs: Record<number, AutoChinRiskConfig> = {};

  constructor(
    strategyId: string,
    accountId: string,
    userId: string,
    bot: AutoChinBotEngine,
  ) {
    this.strategyId = strategyId;
    this.accountId = accountId;
    this.userId = userId;
    this.bot = bot;
    this.edata = new AutoChinEdataEngine(bot);
    this.etask = new AutoChinEtaskEngine(this.edata, userId);
    this.risk = new AutoChinRiskController(bot, this.edata, userId, strategyId, accountId);
    // task6 — order execution (user-triggered, gated by task5).
    this.order = new AutoChinOrderEngine(bot, this.risk, userId, strategyId, accountId);
    // KN1 — task1 trend-candle conversation + "nhắc lại" scheduler.
    this.kn1 = new AutoChinKn1Controller();
    // KN2 — task4 TMA50-cross conversation.
    this.kn2 = new AutoChinKn2Controller();
    // KN3 — task3 SMA50/TMA50 "Tụ Tách" conversation.
    this.kn3 = new AutoChinKn3Controller();
    // Pending orders — "Vào lệnh chờ" (validate price, watch tick-by-tick).
    this.pending = new AutoChinPendingController(bot, this.risk, userId, strategyId, accountId);
    // task7 — secondary alert system (/alert: Price / Reach MA / Break).
    this.alerts = new AutoChinAlertController(bot, this.edata, userId, strategyId, accountId);
  }

  /** Idempotent — safe to call from both the monitor and the background restore. */
  attach(): void {
    if (this.attached) return;
    this.attached = true;

    // Register listeners BEFORE starting the engines: `start()` triggers a
    // synchronous first snapshot.
    this.unsubEvents = this.etask.onEvent((e) => this.handleEvent(e));
    this.unsubRiskEvent = this.risk.onEvent((e) => this.handleRiskEvent(e));

    this.etask.applyConfigs(this.configs);
    this.risk.applyConfigs(this.riskConfigs);
    this.etask.start();
    this.edata.start();
    this.risk.start();
    this.order.start();
    this.kn1.start();
    this.kn2.start();
    this.kn3.start();
    this.pending.start();
    this.alerts.start();
    this.loadConfigs();
  }

  destroy(): void {
    this.edata.stop();
    this.etask.stop();
    this.risk.stop();
    this.order.stop();
    this.kn1.stop();
    this.kn2.stop();
    this.kn3.stop();
    this.pending.stop();
    this.alerts.stop();
    if (this.unsubEvents) { this.unsubEvents(); this.unsubEvents = null; }
    if (this.unsubRiskEvent) { this.unsubRiskEvent(); this.unsubRiskEvent = null; }
    this.attached = false;
    this.listeners.clear();
  }

  applyConfigs(configs: Record<number, AutoChinEtaskConfig>): void {
    this.configs = { ...configs };
    this.etask.applyConfigs(this.configs);
  }

  /** task5 — push Rule 2 (maxOrders) configs into the risk gate. */
  applyRiskConfigs(configs: Record<number, AutoChinRiskConfig>): void {
    this.riskConfigs = { ...configs };
    this.risk.applyConfigs(this.riskConfigs);
  }

  /**
   * task5 — RISK GATE. Call this right before sending an order to cTrader;
   * when `allowed` is false the order must NOT be sent.
   */
  canPlaceOrder(symbolId: number): AutoChinRiskResult {
    return this.risk.canPlaceOrder(symbolId);
  }

  subscribe(fn: (events: AutoChinTaskEvent[]) => void): () => void {
    this.listeners.add(fn);
    fn(this.events);
    return () => { this.listeners.delete(fn); };
  }

  getEvents(): AutoChinTaskEvent[] {
    return this.events;
  }

  // ─── Internal ────────────────────────────────────────────────

  private handleEvent(e: AutoChinTaskEvent): void {
    this.events = [e, ...this.events].slice(0, MAX_EVENTS);
    this.listeners.forEach((fn) => {
      try { fn(this.events); } catch { /* ignore */ }
    });
    // task1 (trend candle) → the interactive KN1 conversation, not a plain ping.
    if (e.type === 'task1_big_candle') {
      void this.kn1.notifyBigCandle(e);
      return;
    }
    // task4 (candle crosses TMA50) → the interactive KN2 conversation.
    if (e.type === 'task4_tma50_cross') {
      void this.kn2.notifyTma50Cross(e);
      return;
    }
    // task3 (SMA50/TMA50 convergence) → the interactive KN3 conversation.
    if (e.type === 'task3_st50_convergence') {
      void this.kn3.notifySt50(e);
      return;
    }
    void this.notifyTelegram(e);
  }

  /**
   * task5 — risk block events. Recorded for the monitor's event feed only.
   *
   * The controller does NOT push Telegram from here: a risk-block alert is sent
   * ONCE per real order attempt by the order path itself (task6's `block-order`
   * / the pending controller's `pause-pending-alert`). Sending it here too would
   * duplicate those alerts AND — worse — fire one message PER SYMBOL on every
   * candle close whenever a single position is open (Rule 3).
   */
  private handleRiskEvent(e: AutoChinTaskEvent): void {
    this.events = [e, ...this.events].slice(0, MAX_EVENTS);
    this.listeners.forEach((fn) => {
      try { fn(this.events); } catch { /* ignore */ }
    });
  }

  /**
   * Part-4 placeholder — a plain Telegram notification, deduped per minute.
   * Part 4 will swap this for the interactive proposal conversation.
   */
  private async notifyTelegram(e: AutoChinTaskEvent): Promise<void> {
    const dedupKey = `${e.type}:${e.symbolId}:${Math.floor(e.at / 60000)}`;
    try {
      await sendTelegram(e.message, {
        category: TELEGRAM_CATEGORIES.AUTOCHIN,
        dedupKey,
      });
    } catch {
      /* best-effort */
    }
  }

  /** Load persisted per-symbol configs (Big Candle) from the session. */
  private async loadConfigs(): Promise<void> {
    try {
      const key = buildSessionKey(this.strategyId, this.accountId);
      const { data } = await supabase
        .from('bot_session_state')
        .select('session_data')
        .eq('user_id', this.userId)
        .eq('strategy_id', key)
        .maybeSingle();

      const sessionData = data?.session_data as Record<string, unknown> | undefined;
      const ws = sessionData?.workspace as Record<string, unknown> | undefined;
      const raw = (ws?.symbolConfigs as Record<string, {
        bigCandlePips?: number;
        st50Pips?: number;
        maxOrders?: number;
      }>) || {};

      const out: Record<number, AutoChinEtaskConfig> = {};
      const riskOut: Record<number, AutoChinRiskConfig> = {};
      Object.entries(raw).forEach(([k, v]) => {
        out[Number(k)] = {
          bigCandlePips: v?.bigCandlePips,
          st50Pips: v?.st50Pips,
        };
        if (v?.maxOrders != null) riskOut[Number(k)] = { maxOrders: v.maxOrders };
      });

      this.configs = out;
      this.etask.applyConfigs(this.configs);
      this.riskConfigs = riskOut;
      this.risk.applyConfigs(this.riskConfigs);
    } catch {
      /* ignore — configs stay as-is */
    }
  }
}

// ─── Per-Account Singleton Registry ────────────────────────────

const runtimes = new Map<string, AutoChinTaskRuntime>();

export function ensureAutoChinTaskRuntime(
  strategyId: string,
  accountId: string,
  userId: string,
): AutoChinTaskRuntime {
  const key = buildSessionKey(strategyId, accountId);
  let rt = runtimes.get(key);
  if (!rt) {
    const bot = getOrCreateAutoChinEngine(strategyId, accountId);
    rt = new AutoChinTaskRuntime(strategyId, accountId, userId, bot);
    runtimes.set(key, rt);
  }
  rt.attach();
  return rt;
}

export function getAutoChinTaskRuntime(strategyId: string, accountId: string): AutoChinTaskRuntime | undefined {
  return runtimes.get(buildSessionKey(strategyId, accountId));
}

export function destroyAutoChinTaskRuntime(strategyId: string, accountId: string): void {
  const key = buildSessionKey(strategyId, accountId);
  const rt = runtimes.get(key);
  if (rt) {
    rt.destroy();
    runtimes.delete(key);
  }
}

export function getAllAutoChinTaskRuntimes(): Map<string, AutoChinTaskRuntime> {
  return runtimes;
}